import { useState } from 'react';

import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';

interface CapabilitiesUnavailableProps {
  /** Resolves to whether the retry produced a capability snapshot. */
  onRetry: () => Promise<boolean>;
}

/**
 * Shown when the partner-capability snapshot could not be loaded.
 *
 * Without it every capability reads as denied, so continuing would strip
 * the navigation and bounce the user off the pages they are entitled to —
 * an outage rendered as a permissions decision. Saying so and offering a
 * retry is the honest state.
 *
 * The retry's own state lives here rather than in the layout: this panel is
 * mounted only while the outage lasts, so it resets with the outage. Held
 * above, it had to be cleared explicitly, and a stale value opened the next
 * outage already claiming a retry had failed.
 */
export function CapabilitiesUnavailable({ onRetry }: CapabilitiesUnavailableProps) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);

  const handleRetry = () => {
    setRetryFailed(false);
    setIsRetrying(true);
    onRetry()
      .then(recovered => {
        setRetryFailed(!recovered);
      })
      .catch(() => {
        // This panel is the only feedback channel, so a rejected retry has to
        // say so rather than leaving the button looking inert.
        setRetryFailed(true);
      })
      .finally(() => {
        setIsRetrying(false);
      });
  };

  return (
    <SDSFlexbox
      direction="column"
      gap="content-default"
      alignItems="flex-start"
      dataTestId={DataTestIds.PARTNER.CAPABILITIES_UNAVAILABLE.PANEL}
    >
      <SDSText type="h2" text="We could not load your permissions" />
      <SDSText
        type="body-medium"
        color="secondary"
        text="Your partner workspace is unavailable right now, so nothing on this page can be shown safely. Try again in a moment."
      />
      {retryFailed ? (
        <SDSText type="body-medium" color="alert" text="Still unavailable. Nothing has changed yet." />
      ) : null}
      <SDSButton
        text={isRetrying ? 'Trying again' : 'Try again'}
        isDisabled={isRetrying}
        onClick={handleRetry}
        dataTestId={DataTestIds.PARTNER.CAPABILITIES_UNAVAILABLE.RETRY}
      />
    </SDSFlexbox>
  );
}
